import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, HStack, Text, Box, Icon, Circle, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiCompass, FiFolder, FiFileText, FiBarChart2, FiSettings, FiCheckCircle,
} from 'react-icons/fi';
import {
  TOUR_SEEN_KEY, TOUR_SEEN_VALUE, REPLAY_TOUR_EVENT,
  shouldAutoShowTour, isLastStep, nextStep, prevStep,
} from './tourState';

// item 123 — in-app guided product tour, extending the first-login tutorial harness
// (items 40/41). A dependency-free step walkthrough of where things are + how the core
// flow works. Auto-shows once after sign-in (localStorage flag); replayable any time via
// the 'basix:replay-tour' window event (Settings → Replay app tour). A DOM-anchored
// spotlight overlay is a future enhancement; this ships the guided content now.

interface Step {
  icon: typeof FiCompass;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: FiCompass,
    title: 'Welcome to Basix',
    body: 'A quick tour of where things live and how the core flow works. Two minutes — you can skip or replay it any time from Settings.',
  },
  {
    icon: FiFolder,
    title: 'Sidebar & Projects',
    body: 'The left sidebar is your nav. Projects are grouped under their customer — expand a customer to see its projects, search across both, and pin favorites. ⌘K opens global search.',
  },
  {
    icon: FiFileText,
    title: 'The revenue flow',
    body: 'Proposal → approve → a Work Order is created → generate the Deposit invoice → Finalize & send → Complete the proposal to bill the final invoice. Sales Orders are hardware-only estimates that convert the same way.',
  },
  {
    icon: FiBarChart2,
    title: 'Reports & dashboard',
    body: 'The KPI strip rides the top of every page. Reports (TOOLS → Reports) cover AR, pipeline, commissions, attribution, inventory, and more — each exports CSV + branded PDF.',
  },
  {
    icon: FiSettings,
    title: 'Settings & appearance',
    body: 'Admins manage branding, terms, fiscal year, and document numbering in Settings. Everyone can set their own appearance (light/dark + skin) and a personal PIN lock.',
  },
  {
    icon: FiCheckCircle,
    title: "You're set",
    body: 'That’s the lay of the land. Replay this any time from Settings → Replay app tour. Let’s make business easier.',
  },
];

const GuidedTour: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const accent = useColorModeValue('brandGreen.500', 'brandGreen.300');
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');

  useEffect(() => {
    // auto-show once per device after sign-in
    if (shouldAutoShowTour(localStorage.getItem(TOUR_SEEN_KEY))) {
      setStep(0);
      setIsOpen(true);
    }
    const replay = () => { setStep(0); setIsOpen(true); };
    window.addEventListener(REPLAY_TOUR_EVENT, replay);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, replay);
  }, []);

  const close = () => {
    localStorage.setItem(TOUR_SEEN_KEY, TOUR_SEEN_VALUE);
    setIsOpen(false);
  };

  const current = STEPS[step];
  const isLast = isLastStep(step, STEPS.length);

  return (
    <Modal isOpen={isOpen} onClose={close} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={3}>
            <Circle size="40px" bg={chipBg} color={accent}><Icon as={current.icon} boxSize="20px" /></Circle>
            <Text>{current.title}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" lineHeight="tall">{current.body}</Text>
          <HStack spacing={2} mt={6} justify="center">
            {STEPS.map((s, i) => (
              <Box
                key={s.title}
                w={i === step ? '22px' : '8px'}
                h="8px"
                borderRadius="full"
                bg={i === step ? accent : chipBg}
                transition="width 0.2s"
              />
            ))}
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" size="sm" mr="auto" onClick={close}>Skip</Button>
          {step > 0 && (
            <Button variant="outline" size="sm" mr={2} onClick={() => setStep((s) => prevStep(s))}>Back</Button>
          )}
          {!isLast && (
            <Button size="sm" onClick={() => setStep((s) => nextStep(s, STEPS.length))}>Next</Button>
          )}
          {isLast && (
            <Button size="sm" onClick={close}>Done</Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GuidedTour;
